<div class="text-center mb-3">
	<h1 class="mb-0">{{ pageTitle }}</h1>
</div>

<ng-container *ngIf="section === 'atob'">
<!-- AtoB digital wallet report -->
<div class="col-lg-12 mb-4">
	<div class="shadow p-3">
		<div class="d-flex justify-content-between align-items-start">
			<div>
				<h4 class="mb-1">AtoB Wallet Report</h4>
				<small class="text-muted">
					Upload the AtoB Digital Wallet Report (.csv). Rows are saved to FactorCloud.AtoBWallet by AtoB ID;
					uploading a newer report updates the existing rows.
				</small>
			</div>
			<div class="text-right text-nowrap ml-3" *ngIf="summary">
				<div><b>{{ summary.atoBWalletCount | number }}</b> wallet(s) saved</div>
				<small class="text-muted" *ngIf="summary.atoBWalletLastImportedDate">
					Last upload {{ summary.atoBWalletLastImportedDate | date : 'short' }}
				</small>
			</div>
		</div>

		<div class="row mt-3">
			<div class="col-md-12" *ngIf="!atoBFile">
				<div
					ng2FileDrop
					[ngClass]="fileDragged ? 'nv-file-over' : ''"
					[uploader]="atoBUploader"
					class="conversion-drop-zone"
					(dragover)="onDragover()"
					(dragleave)="onDragout()"
					(mouseleave)="onDragout()"
					data-testid="conversion-data-upload-atob-drop">
					<label
						for="conversion-atob-file"
						role="button"
						aria-label="Choose the AtoB wallet report"
						class="conversion-file-label m-0 w-100 h-100 d-flex align-items-center justify-content-center">
						<i class="fa fa-cloud-upload mr-2" aria-hidden="true"></i> Drop the AtoB wallet report here, or click to choose it
					</label>
					<input
						id="conversion-atob-file"
						type="file"
						accept=".csv"
						ng2FileSelect
						[uploader]="atoBUploader"
						data-testid="conversion-data-upload-atob-file" />
				</div>
			</div>

			<div class="col-md-3" *ngIf="atoBFile">
				<div class="text-muted">File</div>
				<div class="font-weight-bold conversion-ellipsis">{{ atoBFile.name }}</div>
				<div class="text-muted mt-2">Size</div>
				<div class="font-weight-bold">{{ atoBFile.size / 1024 | number : '1.0-1' }} KB</div>
				<div class="text-muted mt-2">Rows</div>
				<div class="font-weight-bold">{{ previewTotalRows | number }}</div>
				<button
					type="button"
					class="btn btn-primary mt-3 mr-2"
					[disabled]="uploadingAtoB"
					(click)="uploadAtoB()"
					data-testid="conversion-data-upload-atob-upload">
					<i class="fa" [ngClass]="uploadingAtoB ? 'fa-spinner fa-spin' : 'fa-upload'" aria-hidden="true"></i>
					Upload
				</button>
				<button
					type="button"
					class="btn btn-secondary mt-3"
					[disabled]="uploadingAtoB"
					(click)="resetAtoB()"
					data-testid="conversion-data-upload-atob-cancel">
					Cancel
				</button>
			</div>

			<div class="col-md-9" *ngIf="atoBFile && previewColumns.length > 0">
				<div class="font-weight-bold">
					Preview
					<small class="text-muted" *ngIf="previewTotalRows > previewRows.length">(first {{ previewRows.length }} of {{ previewTotalRows }} rows)</small>
				</div>
				<ngx-datatable
					class="material striped conversion-preview"
					[rows]="previewRows"
					[headerHeight]="40"
					[footerHeight]="0"
					[rowHeight]="32"
					[scrollbarH]="true"
					[scrollbarV]="true">
					<ngx-datatable-column *ngFor="let column of previewColumns" [name]="column.name" [prop]="column.prop" [width]="140"></ngx-datatable-column>
				</ngx-datatable>
			</div>
		</div>

		<ng-container *ngTemplateOutlet="resultTemplate; context: { $implicit: atoBResult }"></ng-container>
	</div>
</div>

<!-- AtoB wallet funding instructions -->
<div class="col-lg-12 mb-4">
	<div class="shadow p-3">
		<div>
			<h4 class="mb-1">AtoB Funding Instructions</h4>
			<small class="text-muted">
				Matches each AtoB wallet to the active WinFactor client whose FactorCloud name is the same as the wallet's company name (anything in parentheses is ignored; carriers not in FactorCloud yet are matched on their WinFactor name) and adds an
				"AtoB Wallet" funding instruction (Digital Wallet, AtoB channel, account number = AtoB ID, Pay To = the company name without the
				parentheses) to that client's FactorCloud client. Carriers not in FactorCloud yet get it during the conversion. The client's default
				funding instruction is not changed, and a wallet is never sent twice.
			</small>
		</div>

		<ul class="nav nav-tabs mt-3">
			<li class="nav-item">
				<a
					class="nav-link clickable"
					[ngClass]="atoBTab === 'wallets' ? 'active' : ''"
					role="button"
					(click)="setAtoBTab('wallets')"
					data-testid="conversion-data-upload-atob-tab-wallets">
					Matched Wallets <span class="badge badge-secondary">{{ atoBWallets.length }}</span>
				</a>
			</li>
			<li class="nav-item">
				<a
					class="nav-link clickable"
					[ngClass]="atoBTab === 'unmatched' ? 'active' : ''"
					role="button"
					(click)="setAtoBTab('unmatched')"
					data-testid="conversion-data-upload-atob-tab-unmatched">
					Active WinFactor Clients Without a Wallet <span class="badge badge-secondary">{{ unmatchedClients.length }}</span>
				</a>
			</li>
		</ul>

		<!-- Matched wallets -->
		<ng-container *ngIf="atoBTab === 'wallets'">
			<div class="d-flex justify-content-between align-items-center mt-3">
				<div class="d-flex align-items-center">
					<input
						class="form-control conversion-client-filter mr-2"
						type="text"
						[(ngModel)]="atoBFilter"
						placeholder="AtoB ID, client ID or company..."
						data-testid="conversion-data-upload-atob-filter" />
					<select class="form-control w-auto" [(ngModel)]="atoBMatchFilter" data-testid="conversion-data-upload-atob-match-filter">
						<option value="">All ({{ atoBWallets.length }})</option>
						<option *ngFor="let status of atoBMatchStatuses" [value]="status">{{ status }} ({{ atoBCount(status) }})</option>
					</select>
				</div>
				<div class="d-flex align-items-center">
					<span *ngIf="sendingAtoB" class="text-muted mr-3" data-testid="conversion-data-upload-atob-send-progress">
						<i class="fa-solid fa-spinner fa-spin"></i> Sending {{ atoBSendDone }} / {{ atoBSendTotal }}
					</span>
					<button
						type="button"
						class="btn btn-outline-primary btn-round mr-2"
						title="Refresh"
						[disabled]="loadingAtoBWallets || sendingAtoB"
						(click)="loadAtoBWallets()"
						data-testid="conversion-data-upload-atob-refresh">
						<i class="fa-solid fa-rotate-right"></i>
					</button>
					<button
						type="button"
						class="btn btn-outline-primary mr-2"
						[disabled]="sendingAtoB || selectedAtoBWallets.length === 0"
						(click)="sendSelectedAtoB()"
						data-testid="conversion-data-upload-atob-send-selected">
						Send Selected
						<span *ngIf="selectedAtoBWallets.length > 0">({{ selectedAtoBWallets.length }})</span>
					</button>
					<button
						type="button"
						class="btn btn-primary"
						[disabled]="sendingAtoB || atoBCount('Matched') === 0"
						(click)="sendAllMatchedAtoB()"
						data-testid="conversion-data-upload-atob-send-all">
						Send All Matched ({{ atoBCount('Matched') }})
					</button>
				</div>
			</div>

			<div class="conversion-table-scroll mt-2">
				<table class="table table-sm table-hover mb-0">
					<thead>
						<tr>
							<th class="conversion-check-col">
								<input
									type="checkbox"
									[checked]="allFilteredAtoBSelected"
									[disabled]="sendingAtoB"
									(change)="toggleAllAtoB(!allFilteredAtoBSelected)"
									data-testid="conversion-data-upload-atob-all" />
							</th>
							<th>AtoB ID</th>
							<th>Company Name</th>
							<th>Pay To</th>
							<th>Wallet</th>
							<th>Matched Client</th>
							<th>Match</th>
							<th>Instruction</th>
						</tr>
					</thead>
					<tbody>
						<tr *ngIf="loadingAtoBWallets">
							<td colspan="8" class="text-muted"><i class="fa-solid fa-spinner fa-spin"></i> Loading...</td>
						</tr>
						<tr *ngIf="!loadingAtoBWallets && filteredAtoBWallets.length === 0">
							<td colspan="8" class="text-muted">No wallets match an active WinFactor client. Upload the AtoB wallet report above first.</td>
						</tr>
						<tr *ngFor="let wallet of filteredAtoBWallets; let i = index" [attr.data-testid]="'conversion-data-upload-atob-' + i">
							<td class="conversion-check-col">
								<input
									type="checkbox"
									[(ngModel)]="wallet.selected"
									[disabled]="sendingAtoB || !canSendAtoB(wallet)"
									[attr.data-testid]="'conversion-data-upload-atob-select-' + i" />
							</td>
							<td>{{ wallet.atoBId }}</td>
							<td class="conversion-ellipsis conversion-folder-col" [title]="wallet.companyName">{{ wallet.companyName }}</td>
							<td>{{ wallet.payTo }}</td>
							<td>{{ wallet.walletStatus }}</td>
							<td>
								<div *ngIf="wallet.factorCloudClientName">{{ wallet.factorCloudClientName }}</div>
								<small *ngIf="wallet.winFactorClientId" class="text-muted">
									WinFactor {{ wallet.winFactorClientId }}<span *ngIf="!wallet.factorCloudClientName"> - {{ wallet.winFactorClientName }}</span>
								</small>
								<small
									*ngIf="wallet.matchStatus === 'Ambiguous' && wallet.candidateClients && wallet.candidateClients.length > 0"
									class="text-muted"
									[title]="wallet.candidateClients.join(', ')">
									{{ wallet.candidateClients.length }} active clients share this name
								</small>
							</td>
							<td>
								<span
									[class.text-success]="wallet.matchStatus === 'Matched' || wallet.matchStatus === 'Sent'"
									[class.text-muted]="wallet.matchStatus === 'NotConverted'"
									[class.text-warning]="wallet.matchStatus === 'Ambiguous'"
									[title]="wallet.matchStatus === 'NotConverted' ? 'Not in FactorCloud yet; the conversion adds the AtoB funding instruction.' : ''">
									{{ wallet.matchStatus }}
								</span>
							</td>
							<td>
								<span *ngIf="wallet.fundingInstructionSentDate" class="text-success">Added {{ wallet.fundingInstructionSentDate | date : 'short' }}</span>
								<span *ngIf="!wallet.fundingInstructionSentDate && wallet.sendStatus === 'Failed'" class="text-danger" [title]="wallet.sendMessage">Failed</span>
							</td>
						</tr>
					</tbody>
				</table>
			</div>

			<!-- Failures from the last send -->
			<div class="mt-3" *ngIf="!sendingAtoB && atoBSendFailures.length > 0">
				<div class="text-danger font-weight-bold">{{ atoBSendFailures.length }} wallet(s) failed</div>
				<ul class="mb-0 conversion-problems">
					<li *ngFor="let failure of atoBSendFailures">AtoB {{ failure.atoBId }}<span *ngIf="failure.payTo"> ({{ failure.payTo }})</span>: {{ failure.message }}</li>
				</ul>
			</div>
		</ng-container>

		<!-- Active WinFactor clients without a wallet -->
		<ng-container *ngIf="atoBTab === 'unmatched'">
			<div class="d-flex justify-content-between align-items-center mt-3">
				<input
					class="form-control conversion-client-filter"
					type="text"
					[(ngModel)]="unmatchedFilter"
					placeholder="Client ID or company..."
					data-testid="conversion-data-upload-unmatched-filter" />
				<div class="d-flex align-items-center">
					<button
						type="button"
						class="btn btn-outline-primary mr-2"
						title="Export the clients listed here (with the filter applied) to a CSV file"
						[disabled]="loadingUnmatchedClients || filteredUnmatchedClients.length === 0"
						(click)="exportUnmatchedClients()"
						data-testid="conversion-data-upload-unmatched-export">
						<i class="fa-solid fa-file-csv mr-1"></i>Export ({{ filteredUnmatchedClients.length }})
					</button>
					<button
						type="button"
						class="btn btn-outline-primary btn-round"
						title="Refresh"
						[disabled]="loadingUnmatchedClients"
						(click)="loadUnmatchedClients()"
						data-testid="conversion-data-upload-unmatched-refresh">
						<i class="fa-solid fa-rotate-right"></i>
					</button>
				</div>
			</div>

			<div class="conversion-table-scroll mt-2">
				<table class="table table-sm table-hover mb-0">
					<thead>
						<tr>
							<th>Client ID</th>
							<th>WinFactor Name</th>
							<th>FactorCloud Name</th>
						</tr>
					</thead>
					<tbody>
						<tr *ngIf="loadingUnmatchedClients">
							<td colspan="3" class="text-muted"><i class="fa-solid fa-spinner fa-spin"></i> Loading...</td>
						</tr>
						<tr *ngIf="!loadingUnmatchedClients && filteredUnmatchedClients.length === 0">
							<td colspan="3" class="text-muted">Every active WinFactor client has an AtoB wallet.</td>
						</tr>
						<tr *ngFor="let client of filteredUnmatchedClients; let i = index" [attr.data-testid]="'conversion-data-upload-unmatched-' + i">
							<td>{{ client.winFactorClientId }}</td>
							<td>{{ client.companyName }}</td>
							<td>
								<span *ngIf="client.factorCloudCompanyName">{{ client.factorCloudCompanyName }}</span>
								<span *ngIf="!client.factorCloudCompanyName" class="text-muted">Not converted</span>
							</td>
						</tr>
					</tbody>
				</table>
			</div>
		</ng-container>
	</div>
</div>

</ng-container>

<ng-container *ngIf="section === 'identifiers'">
<!-- WinFactor client identifiers file -->
<div class="col-lg-12 mb-4">
	<div class="shadow p-3">
		<div class="d-flex justify-content-between align-items-start">
			<div>
				<h4 class="mb-1">Client Identifiers File</h4>
				<small class="text-muted">
					Upload the WinFactor client identifiers file (.csv: WinFactor ID, Client Name, AR (Dashboard), Client EIN,
					Client File Number, MC Number, US DOT Number). Rows are saved to WinFactor.ClientIdentifiers by WinFactor ID;
					uploading a newer file updates the existing rows and keeps clients that are not in it.
					The conversion sends the Client EIN as the FactorCloud client's EIN when it creates or updates the client.
				</small>
			</div>
			<div class="text-right text-nowrap ml-3" *ngIf="summary">
				<div><b>{{ summary.clientIdentifierCount | number }}</b> client(s) saved</div>
				<small class="text-muted" *ngIf="summary.clientIdentifierLastImportedDate">
					Last upload {{ summary.clientIdentifierLastImportedDate | date : 'short' }}
				</small>
			</div>
		</div>

		<div class="row mt-3">
			<div class="col-md-12" *ngIf="!atoBFile">
				<div
					ng2FileDrop
					[ngClass]="fileDragged ? 'nv-file-over' : ''"
					[uploader]="atoBUploader"
					class="conversion-drop-zone"
					(dragover)="onDragover()"
					(dragleave)="onDragout()"
					(mouseleave)="onDragout()"
					data-testid="conversion-data-upload-identifiers-drop">
					<label
						for="conversion-identifiers-file"
						role="button"
						aria-label="Choose the client identifiers file"
						class="conversion-file-label m-0 w-100 h-100 d-flex align-items-center justify-content-center">
						<i class="fa fa-cloud-upload mr-2" aria-hidden="true"></i> Drop the client identifiers file here, or click to choose it
					</label>
					<input
						id="conversion-identifiers-file"
						type="file"
						accept=".csv"
						ng2FileSelect
						[uploader]="atoBUploader"
						data-testid="conversion-data-upload-identifiers-file" />
				</div>
			</div>

			<div class="col-md-3" *ngIf="atoBFile">
				<div class="text-muted">File</div>
				<div class="font-weight-bold conversion-ellipsis">{{ atoBFile.name }}</div>
				<div class="text-muted mt-2">Size</div>
				<div class="font-weight-bold">{{ atoBFile.size / 1024 | number : '1.0-1' }} KB</div>
				<div class="text-muted mt-2">Rows</div>
				<div class="font-weight-bold">{{ previewTotalRows | number }}</div>
				<button
					type="button"
					class="btn btn-primary mt-3 mr-2"
					[disabled]="uploadingIdentifiers"
					(click)="uploadIdentifiers()"
					data-testid="conversion-data-upload-identifiers-upload">
					<i class="fa" [ngClass]="uploadingIdentifiers ? 'fa-spinner fa-spin' : 'fa-upload'" aria-hidden="true"></i>
					Upload
				</button>
				<button
					type="button"
					class="btn btn-secondary mt-3"
					[disabled]="uploadingIdentifiers"
					(click)="resetAtoB()"
					data-testid="conversion-data-upload-identifiers-cancel">
					Cancel
				</button>
			</div>

			<div class="col-md-9" *ngIf="atoBFile && previewColumns.length > 0">
				<div class="font-weight-bold">
					Preview
					<small class="text-muted" *ngIf="previewTotalRows > previewRows.length">(first {{ previewRows.length }} of {{ previewTotalRows }} rows)</small>
				</div>
				<ngx-datatable
					class="material striped conversion-preview"
					[rows]="previewRows"
					[headerHeight]="40"
					[footerHeight]="0"
					[rowHeight]="32"
					[scrollbarH]="true"
					[scrollbarV]="true">
					<ngx-datatable-column *ngFor="let column of previewColumns" [name]="column.name" [prop]="column.prop" [width]="140"></ngx-datatable-column>
				</ngx-datatable>
			</div>
		</div>

		<ng-container *ngTemplateOutlet="resultTemplate; context: { $implicit: identifiersResult }"></ng-container>
	</div>
</div>

<!-- Saved client identifiers -->
<div class="col-lg-12 mb-4">
	<div class="shadow p-3">
		<h4 class="mb-1">Saved Client Identifiers</h4>
		<small class="text-muted">The rows in WinFactor.ClientIdentifiers.</small>

		<div class="d-flex justify-content-between align-items-center mt-3">
			<input
				class="form-control conversion-client-filter"
				type="text"
				[(ngModel)]="identifierFilter"
				placeholder="WinFactor ID, name, EIN, MC or DOT..."
				data-testid="conversion-data-upload-identifiers-filter" />
			<div class="d-flex align-items-center">
				<span class="text-muted mr-3">{{ filteredClientIdentifiers.length | number }} of {{ clientIdentifiers.length | number }}</span>
				<button
					type="button"
					class="btn btn-outline-primary btn-round"
					title="Refresh"
					[disabled]="loadingClientIdentifiers"
					(click)="loadClientIdentifiers()"
					data-testid="conversion-data-upload-identifiers-refresh">
					<i class="fa-solid fa-rotate-right"></i>
				</button>
			</div>
		</div>

		<div class="conversion-table-scroll mt-2">
			<table class="table table-sm table-hover mb-0">
				<thead>
					<tr>
						<th>WinFactor ID</th>
						<th>Client Name</th>
						<th class="text-right">AR (Dashboard)</th>
						<th>Client EIN</th>
						<th>Client File #</th>
						<th>MC #</th>
						<th>US DOT #</th>
						<th>Uploaded</th>
					</tr>
				</thead>
				<tbody>
					<tr *ngIf="loadingClientIdentifiers">
						<td colspan="8" class="text-muted"><i class="fa-solid fa-spinner fa-spin"></i> Loading...</td>
					</tr>
					<tr *ngIf="!loadingClientIdentifiers && filteredClientIdentifiers.length === 0">
						<td colspan="8" class="text-muted">No client identifiers saved yet.</td>
					</tr>
					<tr *ngFor="let identifier of filteredClientIdentifiers; let i = index" [attr.data-testid]="'conversion-data-upload-identifier-' + i">
						<td>{{ identifier.winFactorClientId }}</td>
						<td>{{ identifier.clientName }}</td>
						<td class="text-right">
							<span *ngIf="identifier.arDashboardAmount !== null && identifier.arDashboardAmount !== undefined">{{ '$' }}{{ identifier.arDashboardAmount | number : '1.0-2' }}</span>
						</td>
						<td>{{ identifier.clientEin }}</td>
						<td>{{ identifier.clientFileNumber }}</td>
						<td>{{ identifier.mcNumber }}</td>
						<td>{{ identifier.usDotNumber }}</td>
						<td class="text-nowrap">{{ identifier.importedDate | date : 'short' }}</td>
					</tr>
				</tbody>
			</table>
		</div>
	</div>
</div>

</ng-container>

<ng-container *ngIf="section === 'terms'">
<!-- Transfer terms file (FactorCloud.TermsLookup) -->
<div class="col-lg-12 mb-4">
	<div class="shadow p-3">
		<div class="d-flex justify-content-between align-items-start">
			<div>
				<h4 class="mb-1">Transfer Terms File</h4>
				<small class="text-muted">
					Upload the transfer terms file (.xlsx, .xls or .csv; the first sheet with ClientID, Company Name and Transfer Term Name, such as
					the 'Final Term List' sheet of Client Terms.xlsx; optionally ACH Fee and Wire Fee in dollars, which conversion step 3 puts on
					the FactorCloud client, and Term Name, the live term conversion step 20 moves the FactorCloud client to).
					Rows are saved to FactorCloud.TermsLookup by WinFactor ClientID; uploading a newer file updates the existing rows
					and keeps clients that are not in it. The conversion puts each client on its term here (found by name in FactorCloud);
					a client with no row uses NEW SYSTEM TRANSFER.
				</small>
			</div>
			<div class="text-right text-nowrap ml-3" *ngIf="summary">
				<div><b>{{ summary.termsLookupCount | number }}</b> client(s) saved</div>
				<small class="text-muted" *ngIf="summary.termsLookupLastImportedDate">
					Last upload {{ summary.termsLookupLastImportedDate | date : 'short' }}
				</small>
			</div>
		</div>

		<div class="row mt-3">
			<div class="col-md-12" *ngIf="!atoBFile">
				<div
					ng2FileDrop
					[ngClass]="fileDragged ? 'nv-file-over' : ''"
					[uploader]="atoBUploader"
					class="conversion-drop-zone"
					(dragover)="onDragover()"
					(dragleave)="onDragout()"
					(mouseleave)="onDragout()"
					data-testid="conversion-data-upload-terms-drop">
					<label
						for="conversion-terms-file"
						role="button"
						aria-label="Choose the transfer terms file"
						class="conversion-file-label m-0 w-100 h-100 d-flex align-items-center justify-content-center">
						<i class="fa fa-cloud-upload mr-2" aria-hidden="true"></i> Drop the transfer terms file here, or click to choose it
					</label>
					<input
						id="conversion-terms-file"
						type="file"
						accept=".xlsx,.xls,.csv"
						ng2FileSelect
						[uploader]="atoBUploader"
						data-testid="conversion-data-upload-terms-file" />
				</div>
			</div>

			<div class="col-md-3" *ngIf="atoBFile">
				<div class="text-muted">File</div>
				<div class="font-weight-bold conversion-ellipsis">{{ atoBFile.name }}</div>
				<div class="text-muted mt-2">Size</div>
				<div class="font-weight-bold">{{ atoBFile.size / 1024 | number : '1.0-1' }} KB</div>
				<ng-container *ngIf="previewColumns.length > 0">
					<div class="text-muted mt-2">Rows</div>
					<div class="font-weight-bold">{{ previewTotalRows | number }}</div>
				</ng-container>
				<button
					type="button"
					class="btn btn-primary mt-3 mr-2"
					[disabled]="uploadingTerms"
					(click)="uploadTerms()"
					data-testid="conversion-data-upload-terms-upload">
					<i class="fa" [ngClass]="uploadingTerms ? 'fa-spinner fa-spin' : 'fa-upload'" aria-hidden="true"></i>
					Upload
				</button>
				<button
					type="button"
					class="btn btn-secondary mt-3"
					[disabled]="uploadingTerms"
					(click)="resetAtoB()"
					data-testid="conversion-data-upload-terms-cancel">
					Cancel
				</button>
			</div>

			<div class="col-md-9" *ngIf="atoBFile && previewColumns.length > 0">
				<div class="font-weight-bold">
					Preview
					<small class="text-muted" *ngIf="previewTotalRows > previewRows.length">(first {{ previewRows.length }} of {{ previewTotalRows }} rows)</small>
				</div>
				<ngx-datatable
					class="material striped conversion-preview"
					[rows]="previewRows"
					[headerHeight]="40"
					[footerHeight]="0"
					[rowHeight]="32"
					[scrollbarH]="true"
					[scrollbarV]="true">
					<ngx-datatable-column *ngFor="let column of previewColumns" [name]="column.name" [prop]="column.prop" [width]="140"></ngx-datatable-column>
				</ngx-datatable>
			</div>
			<div class="col-md-9 text-muted" *ngIf="atoBFile && previewColumns.length === 0">
				Excel files are read when you upload them; there is no preview.
			</div>
		</div>

		<ng-container *ngTemplateOutlet="resultTemplate; context: { $implicit: termsResult }"></ng-container>
	</div>
</div>

<!-- Saved transfer terms -->
<div class="col-lg-12 mb-4">
	<div class="shadow p-3">
		<h4 class="mb-1">Saved Transfer Terms</h4>
		<small class="text-muted">The rows in FactorCloud.TermsLookup.</small>

		<div class="d-flex justify-content-between align-items-center mt-3">
			<div class="d-flex align-items-center">
				<input
					class="form-control conversion-client-filter mr-3"
					type="text"
					[(ngModel)]="termsFilter"
					placeholder="ClientID, company or term..."
					data-testid="conversion-data-upload-terms-filter" />
				<label class="mb-0 clickable text-nowrap">
					<input
						type="checkbox"
						[(ngModel)]="termsOverridesOnly"
						data-testid="conversion-data-upload-terms-overrides-only" />
					Only terms other than NEW SYSTEM TRANSFER ({{ termsOverrideCount | number }})
				</label>
			</div>
			<div class="d-flex align-items-center">
				<span class="text-muted mr-3">{{ filteredTermsLookups.length | number }} of {{ termsLookups.length | number }}</span>
				<button
					type="button"
					class="btn btn-outline-primary btn-round"
					title="Refresh"
					[disabled]="loadingTermsLookups"
					(click)="loadTermsLookups()"
					data-testid="conversion-data-upload-terms-refresh">
					<i class="fa-solid fa-rotate-right"></i>
				</button>
			</div>
		</div>

		<div class="conversion-table-scroll mt-2">
			<table class="table table-sm table-hover mb-0">
				<thead>
					<tr>
						<th>ClientID</th>
						<th>Company Name</th>
						<th>Transfer Term Name</th>
						<th>Live Term Name</th>
						<th class="text-right">ACH Fee</th>
						<th class="text-right">Wire Fee</th>
						<th>Uploaded</th>
					</tr>
				</thead>
				<tbody>
					<tr *ngIf="loadingTermsLookups">
						<td colspan="7" class="text-muted"><i class="fa-solid fa-spinner fa-spin"></i> Loading...</td>
					</tr>
					<tr *ngIf="!loadingTermsLookups && filteredTermsLookups.length === 0">
						<td colspan="7" class="text-muted">No transfer terms saved yet.</td>
					</tr>
					<tr *ngFor="let lookup of filteredTermsLookups; let i = index" [attr.data-testid]="'conversion-data-upload-term-' + i">
						<td>{{ lookup.winFactorClientId }}</td>
						<td>{{ lookup.companyName }}</td>
						<td [ngClass]="isTermOverride(lookup) ? 'font-weight-bold' : ''">{{ lookup.transferTermName }}</td>
						<td>{{ lookup.liveTermName }}</td>
						<td class="text-right">{{ lookup.achFee === null || lookup.achFee === undefined ? '' : (lookup.achFee | currency : 'USD') }}</td>
						<td class="text-right">{{ lookup.wireFee === null || lookup.wireFee === undefined ? '' : (lookup.wireFee | currency : 'USD') }}</td>
						<td class="text-nowrap">{{ lookup.importedDate | date : 'short' }}</td>
					</tr>
				</tbody>
			</table>
		</div>
	</div>
</div>

</ng-container>

<ng-container *ngIf="section === 'funding'">
<!-- WinFactor Funding Options file (secure.WFBD) -->
<div class="col-lg-12 mb-4">
	<div class="shadow p-3">
		<div class="d-flex justify-content-between align-items-start">
			<div>
				<h4 class="mb-1">Funding Options File</h4>
				<small class="text-muted">
					Upload the WinFactor Funding Options export (.xlsx, .xls or .csv, first sheet: WinFactor Client ID, Client Name, Option #,
					Funding Type, bank details, Active ...). Rows are saved to secure.WFBD by WinFactor Client ID and Option #; uploading a newer
					file updates the existing rows and keeps options that are not in it. When a carrier converts, each active WIRE or ACH option is added to its
					FactorCloud client as a funding instruction (the Aladdin CHECK instruction stays the default). An option already sent is only
					sent again if its funding type, account or routing number changed.
				</small>
			</div>
			<div class="text-right text-nowrap ml-3" *ngIf="summary">
				<div><b>{{ summary.fundingOptionCount | number }}</b> option(s) saved</div>
				<small class="text-muted" *ngIf="summary.fundingOptionLastImportedDate">
					Last upload {{ summary.fundingOptionLastImportedDate | date : 'short' }}
				</small>
			</div>
		</div>

		<div class="row mt-3">
			<div class="col-md-12" *ngIf="!atoBFile">
				<div
					ng2FileDrop
					[ngClass]="fileDragged ? 'nv-file-over' : ''"
					[uploader]="atoBUploader"
					class="conversion-drop-zone"
					(dragover)="onDragover()"
					(dragleave)="onDragout()"
					(mouseleave)="onDragout()"
					data-testid="conversion-data-upload-funding-drop">
					<label
						for="conversion-funding-file"
						role="button"
						aria-label="Choose the funding options file"
						class="conversion-file-label m-0 w-100 h-100 d-flex align-items-center justify-content-center">
						<i class="fa fa-cloud-upload mr-2" aria-hidden="true"></i> Drop the WinFactor Funding Options file here, or click to choose it
					</label>
					<input
						id="conversion-funding-file"
						type="file"
						accept=".xlsx,.xls,.csv"
						ng2FileSelect
						[uploader]="atoBUploader"
						data-testid="conversion-data-upload-funding-file" />
				</div>
			</div>

			<div class="col-md-3" *ngIf="atoBFile">
				<div class="text-muted">File</div>
				<div class="font-weight-bold conversion-ellipsis">{{ atoBFile.name }}</div>
				<div class="text-muted mt-2">Size</div>
				<div class="font-weight-bold">{{ atoBFile.size / 1024 | number : '1.0-1' }} KB</div>
				<ng-container *ngIf="previewColumns.length > 0">
					<div class="text-muted mt-2">Rows</div>
					<div class="font-weight-bold">{{ previewTotalRows | number }}</div>
				</ng-container>
				<button
					type="button"
					class="btn btn-primary mt-3 mr-2"
					[disabled]="uploadingFunding"
					(click)="uploadFunding()"
					data-testid="conversion-data-upload-funding-upload">
					<i class="fa" [ngClass]="uploadingFunding ? 'fa-spinner fa-spin' : 'fa-upload'" aria-hidden="true"></i>
					Upload
				</button>
				<button
					type="button"
					class="btn btn-secondary mt-3"
					[disabled]="uploadingFunding"
					(click)="resetAtoB()"
					data-testid="conversion-data-upload-funding-cancel">
					Cancel
				</button>
			</div>

			<div class="col-md-9" *ngIf="atoBFile && previewColumns.length > 0">
				<div class="font-weight-bold">
					Preview
					<small class="text-muted" *ngIf="previewTotalRows > previewRows.length">(first {{ previewRows.length }} of {{ previewTotalRows }} rows)</small>
				</div>
				<ngx-datatable
					class="material striped conversion-preview"
					[rows]="previewRows"
					[headerHeight]="40"
					[footerHeight]="0"
					[rowHeight]="32"
					[scrollbarH]="true"
					[scrollbarV]="true">
					<ngx-datatable-column *ngFor="let column of previewColumns" [name]="column.name" [prop]="column.prop" [width]="140"></ngx-datatable-column>
				</ngx-datatable>
			</div>
			<div class="col-md-9 text-muted" *ngIf="atoBFile && previewColumns.length === 0">
				Excel files are read when you upload them; there is no preview.
			</div>
		</div>

		<ng-container *ngTemplateOutlet="resultTemplate; context: { $implicit: fundingResult }"></ng-container>
	</div>
</div>

<!-- Saved funding options -->
<div class="col-lg-12 mb-4">
	<div class="shadow p-3">
		<h4 class="mb-1">Saved Funding Options</h4>
		<small class="text-muted">The rows in secure.WFBD. Account and routing numbers show their last four digits only.</small>

		<div class="d-flex justify-content-between align-items-center mt-3">
			<input
				class="form-control conversion-client-filter"
				type="text"
				[(ngModel)]="fundingFilter"
				placeholder="Client ID, client, funding type or bank..."
				data-testid="conversion-data-upload-funding-filter" />
			<div class="d-flex align-items-center">
				<span class="text-muted mr-3">{{ filteredFundingOptions.length | number }} of {{ fundingOptions.length | number }}</span>
				<button
					type="button"
					class="btn btn-outline-primary btn-round"
					title="Refresh"
					[disabled]="loadingFundingOptions"
					(click)="loadFundingOptions()"
					data-testid="conversion-data-upload-funding-refresh">
					<i class="fa-solid fa-rotate-right"></i>
				</button>
			</div>
		</div>

		<div class="conversion-table-scroll mt-2">
			<table class="table table-sm table-hover mb-0">
				<thead>
					<tr>
						<th>Client ID</th>
						<th>Client Name</th>
						<th class="text-center">Option #</th>
						<th>Funding Type</th>
						<th>Account Name</th>
						<th>Beneficiary</th>
						<th>Bank</th>
						<th>Account Type</th>
						<th>Account #</th>
						<th>Routing #</th>
						<th class="text-center">Active</th>
						<th>Sent to FactorCloud</th>
						<th>Uploaded</th>
					</tr>
				</thead>
				<tbody>
					<tr *ngIf="loadingFundingOptions">
						<td colspan="13" class="text-muted"><i class="fa-solid fa-spinner fa-spin"></i> Loading...</td>
					</tr>
					<tr *ngIf="!loadingFundingOptions && filteredFundingOptions.length === 0">
						<td colspan="13" class="text-muted">No funding options saved yet.</td>
					</tr>
					<tr *ngFor="let option of filteredFundingOptions; let i = index" [attr.data-testid]="'conversion-data-upload-funding-option-' + i">
						<td>{{ option.winFactorClientId }}</td>
						<td>{{ option.clientName }}</td>
						<td class="text-center">{{ option.optionNumber }}</td>
						<td>{{ option.fundingType }}<span *ngIf="option.wireType" class="text-muted"> ({{ option.wireType }})</span></td>
						<td>{{ option.accountName }}</td>
						<td>{{ option.beneficiaryName }}</td>
						<td>{{ option.bankName }}</td>
						<td>{{ option.bankAccountType }}</td>
						<td class="text-nowrap">{{ option.bankAccountNumber }}</td>
						<td class="text-nowrap">{{ option.abaRoutingNumber }}</td>
						<td class="text-center">
							<i class="fa-solid" [ngClass]="option.isActive ? 'fa-check text-success' : 'fa-xmark text-muted'"></i>
						</td>
						<td class="text-nowrap">
							<span *ngIf="option.sentToFactorCloudDate" [attr.title]="option.factorCloudFundingInstructionId">{{ option.sentToFactorCloudDate | date : 'short' }}</span>
							<span *ngIf="!option.sentToFactorCloudDate" class="text-muted">Not yet</span>
						</td>
						<td class="text-nowrap">{{ option.importedDate | date : 'short' }}</td>
					</tr>
				</tbody>
			</table>
		</div>
	</div>
</div>

</ng-container>

<ng-container *ngIf="section === 'relationships'">
<!-- WinFactor client debtor relationships file (WinFactor.ClientDebtorRelationships) -->
<div class="col-lg-12 mb-4">
	<div class="shadow p-3">
		<div class="d-flex justify-content-between align-items-start">
			<div>
				<h4 class="mb-1">Client Debtor Relationships File</h4>
				<small class="text-muted">
					Upload the WinFactor client/customer relationships export (.xlsx, .xls or .csv, first sheet: ID, ClientID, ClientName, CustomerID,
					CustomerName, OverrideCreditLimit, OverrideCreditLimitOption, RelationshipDoNotBuy ...). Rows are saved to
					WinFactor.ClientDebtorRelationships by ID; uploading a newer file updates the existing rows and keeps relationships that are not in it.
					When the conversion creates or updates a client/debtor relationship in FactorCloud, No Buy comes from RelationshipDoNotBuy and,
					when OverrideCreditLimitOption is 1, the credit limit is OverrideCreditLimit. A full file (about 250,000 rows) takes a minute or two.
				</small>
			</div>
			<div class="text-right text-nowrap ml-3" *ngIf="summary">
				<div><b>{{ summary.relationshipCount | number }}</b> relationship(s) saved</div>
				<small class="text-muted" *ngIf="summary.relationshipLastImportedDate">
					Last upload {{ summary.relationshipLastImportedDate | date : 'short' }}
				</small>
			</div>
		</div>

		<div class="row mt-3">
			<div class="col-md-12" *ngIf="!atoBFile">
				<div
					ng2FileDrop
					[ngClass]="fileDragged ? 'nv-file-over' : ''"
					[uploader]="atoBUploader"
					class="conversion-drop-zone"
					(dragover)="onDragover()"
					(dragleave)="onDragout()"
					(mouseleave)="onDragout()"
					data-testid="conversion-data-upload-relationships-drop">
					<label
						for="conversion-relationships-file"
						role="button"
						aria-label="Choose the client debtor relationships file"
						class="conversion-file-label m-0 w-100 h-100 d-flex align-items-center justify-content-center">
						<i class="fa fa-cloud-upload mr-2" aria-hidden="true"></i> Drop the WinFactor client debtor relationships file here, or click to choose it
					</label>
					<input
						id="conversion-relationships-file"
						type="file"
						accept=".xlsx,.xls,.csv"
						ng2FileSelect
						[uploader]="atoBUploader"
						data-testid="conversion-data-upload-relationships-file" />
				</div>
			</div>

			<div class="col-md-3" *ngIf="atoBFile">
				<div class="text-muted">File</div>
				<div class="font-weight-bold conversion-ellipsis">{{ atoBFile.name }}</div>
				<div class="text-muted mt-2">Size</div>
				<div class="font-weight-bold">{{ atoBFile.size / 1024 | number : '1.0-1' }} KB</div>
				<ng-container *ngIf="previewColumns.length > 0">
					<div class="text-muted mt-2">Rows</div>
					<div class="font-weight-bold">{{ previewTotalRows | number }}</div>
				</ng-container>
				<button
					type="button"
					class="btn btn-primary mt-3 mr-2"
					[disabled]="uploadingRelationships"
					(click)="uploadRelationships()"
					data-testid="conversion-data-upload-relationships-upload">
					<i class="fa" [ngClass]="uploadingRelationships ? 'fa-spinner fa-spin' : 'fa-upload'" aria-hidden="true"></i>
					Upload
				</button>
				<button
					type="button"
					class="btn btn-secondary mt-3"
					[disabled]="uploadingRelationships"
					(click)="resetAtoB()"
					data-testid="conversion-data-upload-relationships-cancel">
					Cancel
				</button>
			</div>

			<div class="col-md-9" *ngIf="atoBFile && previewColumns.length > 0">
				<div class="font-weight-bold">
					Preview
					<small class="text-muted" *ngIf="previewTotalRows > previewRows.length">(first {{ previewRows.length }} of {{ previewTotalRows }} rows)</small>
				</div>
				<ngx-datatable
					class="material striped conversion-preview"
					[rows]="previewRows"
					[headerHeight]="40"
					[footerHeight]="0"
					[rowHeight]="32"
					[scrollbarH]="true"
					[scrollbarV]="true">
					<ngx-datatable-column *ngFor="let column of previewColumns" [name]="column.name" [prop]="column.prop" [width]="140"></ngx-datatable-column>
				</ngx-datatable>
			</div>
			<div class="col-md-9 text-muted" *ngIf="atoBFile && previewColumns.length === 0">
				Excel files are read when you upload them; there is no preview.
			</div>
		</div>

		<ng-container *ngTemplateOutlet="resultTemplate; context: { $implicit: relationshipsResult }"></ng-container>
	</div>
</div>

<!-- Saved client debtor relationships -->
<div class="col-lg-12 mb-4">
	<div class="shadow p-3">
		<h4 class="mb-1">Saved Client Debtor Relationships</h4>
		<small class="text-muted">The rows in WinFactor.ClientDebtorRelationships (the first {{ relationshipRowLimit }} matching the search).</small>

		<div class="d-flex justify-content-between align-items-center mt-3">
			<div class="d-flex align-items-center">
				<input
					class="form-control conversion-client-filter mr-2"
					type="text"
					[(ngModel)]="relationshipSearch"
					(keyup.enter)="loadRelationships()"
					placeholder="Client / customer ID or name..."
					data-testid="conversion-data-upload-relationships-search" />
				<button
					type="button"
					class="btn btn-primary"
					[disabled]="loadingRelationships"
					(click)="loadRelationships()"
					data-testid="conversion-data-upload-relationships-search-button">
					Search
				</button>
			</div>
			<span class="text-muted">{{ relationships.length | number }} shown</span>
		</div>

		<div class="conversion-table-scroll mt-2">
			<table class="table table-sm table-hover mb-0">
				<thead>
					<tr>
						<th>ID</th>
						<th>Client ID</th>
						<th>Client Name</th>
						<th>Customer ID</th>
						<th>Customer Name</th>
						<th class="text-center">No Buy</th>
						<th class="text-center">Override Credit Limit</th>
						<th class="text-right">Override Credit Limit Amount</th>
						<th>Representative</th>
						<th>Uploaded</th>
					</tr>
				</thead>
				<tbody>
					<tr *ngIf="loadingRelationships">
						<td colspan="10" class="text-muted"><i class="fa-solid fa-spinner fa-spin"></i> Loading...</td>
					</tr>
					<tr *ngIf="!loadingRelationships && relationships.length === 0">
						<td colspan="10" class="text-muted">No client debtor relationships found.</td>
					</tr>
					<tr *ngFor="let relationship of relationships; let i = index" [attr.data-testid]="'conversion-data-upload-relationship-' + i">
						<td>{{ relationship.relationshipId }}</td>
						<td>{{ relationship.clientId }}</td>
						<td>{{ relationship.clientName }}</td>
						<td>{{ relationship.customerId }}</td>
						<td>{{ relationship.customerName }}</td>
						<td class="text-center">
							<span [ngClass]="relationship.relationshipDoNotBuy ? 'text-danger font-weight-bold' : 'text-muted'">{{ relationship.relationshipDoNotBuy ? 'Yes' : 'No' }}</span>
						</td>
						<td class="text-center">{{ relationship.overrideCreditLimitOption === 1 ? 'Yes' : 'No' }}</td>
						<td class="text-right">
							<span *ngIf="relationship.overrideCreditLimit !== null && relationship.overrideCreditLimit !== undefined">{{ '$' }}{{ relationship.overrideCreditLimit | number : '1.2-2' }}</span>
						</td>
						<td>{{ relationship.representativeName }}</td>
						<td class="text-nowrap">{{ relationship.importedDate | date : 'short' }}</td>
					</tr>
				</tbody>
			</table>
		</div>
	</div>
</div>

</ng-container>

<ng-container *ngIf="section === 'ties'">
<!-- WinFactor "Active Clients with Customer Info" report (WinFactor.ClientDebtorTies) -->
<div class="col-lg-12 mb-4">
	<div class="shadow p-3">
		<div class="d-flex justify-content-between align-items-start">
			<div>
				<h4 class="mb-1">Client Debtor Ties File</h4>
				<small class="text-muted">
					Upload the WinFactor "Active Clients with Customer Info" report (.xlsx, .xls or .csv, first sheet), either flat (a header row with
					ClientName and CustomerName columns, one row per client/customer) or as printed (each client's name with "Contact:" beside it, then
					its customers under the Customer / Contact / Phone / Fax header). The list is saved to
					WinFactor.ClientDebtorTies and replaces the previous upload. Client names are matched to WinFactor clients and customer names to
					WinFactor customers. When a client is converted, step 2 links it in FactorCloud to every debtor on its list; a customer that cannot
					be matched is listed in step 2 and the conversion carries on. A full report (about 250,000 rows) takes a minute or two.
				</small>
			</div>
			<div class="text-right text-nowrap ml-3" *ngIf="summary">
				<div><b>{{ summary.tieCount | number }}</b> tie(s) for <b>{{ summary.tieClientCount | number }}</b> client(s) saved</div>
				<small class="text-muted" *ngIf="summary.tieLastImportedDate">
					Last upload {{ summary.tieLastImportedDate | date : 'short' }}
				</small>
			</div>
		</div>

		<div class="row mt-3">
			<div class="col-md-12" *ngIf="!atoBFile">
				<div
					ng2FileDrop
					[ngClass]="fileDragged ? 'nv-file-over' : ''"
					[uploader]="atoBUploader"
					class="conversion-drop-zone"
					(dragover)="onDragover()"
					(dragleave)="onDragout()"
					(mouseleave)="onDragout()"
					data-testid="conversion-data-upload-ties-drop">
					<label
						for="conversion-ties-file"
						role="button"
						aria-label="Choose the client debtor ties file"
						class="conversion-file-label m-0 w-100 h-100 d-flex align-items-center justify-content-center">
						<i class="fa fa-cloud-upload mr-2" aria-hidden="true"></i> Drop the WinFactor Active Clients with Customer Info report here, or click to choose it
					</label>
					<input
						id="conversion-ties-file"
						type="file"
						accept=".xlsx,.xls,.csv"
						ng2FileSelect
						[uploader]="atoBUploader"
						data-testid="conversion-data-upload-ties-file" />
				</div>
			</div>

			<div class="col-md-3" *ngIf="atoBFile">
				<div class="text-muted">File</div>
				<div class="font-weight-bold conversion-ellipsis">{{ atoBFile.name }}</div>
				<div class="text-muted mt-2">Size</div>
				<div class="font-weight-bold">{{ atoBFile.size / 1024 | number : '1.0-1' }} KB</div>
				<button
					type="button"
					class="btn btn-primary mt-3 mr-2"
					[disabled]="uploadingTies"
					(click)="uploadTies()"
					data-testid="conversion-data-upload-ties-upload">
					<i class="fa" [ngClass]="uploadingTies ? 'fa-spinner fa-spin' : 'fa-upload'" aria-hidden="true"></i>
					Upload
				</button>
				<button
					type="button"
					class="btn btn-secondary mt-3"
					[disabled]="uploadingTies"
					(click)="resetAtoB()"
					data-testid="conversion-data-upload-ties-cancel">
					Cancel
				</button>
			</div>

			<div class="col-md-9 text-muted" *ngIf="atoBFile">
				The report is read when you upload it; there is no preview.
			</div>
		</div>

		<ng-container *ngTemplateOutlet="resultTemplate; context: { $implicit: tiesResult }"></ng-container>
	</div>
</div>

<!-- Saved client debtor ties -->
<div class="col-lg-12 mb-4">
	<div class="shadow p-3">
		<h4 class="mb-1">Saved Client Debtor Ties</h4>
		<small class="text-muted">The rows in WinFactor.ClientDebtorTies (the first {{ relationshipRowLimit }} matching the search).</small>

		<div class="d-flex justify-content-between align-items-center mt-3">
			<div class="d-flex align-items-center">
				<input
					class="form-control conversion-client-filter mr-2"
					type="text"
					[(ngModel)]="tieSearch"
					(keyup.enter)="loadTies()"
					placeholder="Client / customer name..."
					data-testid="conversion-data-upload-ties-search" />
				<button
					type="button"
					class="btn btn-primary"
					[disabled]="loadingTies"
					(click)="loadTies()"
					data-testid="conversion-data-upload-ties-search-button">
					Search
				</button>
			</div>
			<span class="text-muted">{{ ties.length | number }} shown</span>
		</div>

		<div class="conversion-table-scroll mt-2">
			<table class="table table-sm table-hover mb-0">
				<thead>
					<tr>
						<th>Client Name</th>
						<th>WinFactor Client ID</th>
						<th>Customer Name</th>
						<th>WinFactor Customer ID</th>
						<th>Debtor Key</th>
						<th>Conversion</th>
						<th>Uploaded</th>
					</tr>
				</thead>
				<tbody>
					<tr *ngIf="loadingTies">
						<td colspan="7" class="text-muted"><i class="fa-solid fa-spinner fa-spin"></i> Loading...</td>
					</tr>
					<tr *ngIf="!loadingTies && ties.length === 0">
						<td colspan="7" class="text-muted">No client debtor ties found.</td>
					</tr>
					<tr *ngFor="let tie of ties; let i = index" [attr.data-testid]="'conversion-data-upload-tie-' + i">
						<td>{{ tie.clientName }}</td>
						<td>
							<span *ngIf="tie.winFactorClientId; else noTieClient">{{ tie.winFactorClientId }}</span>
							<ng-template #noTieClient><span class="text-danger">Not matched</span></ng-template>
						</td>
						<td>{{ tie.customerName }}</td>
						<td>
							<span *ngIf="tie.winFactorCustomerId; else noTieCustomer">{{ tie.winFactorCustomerId }}</span>
							<ng-template #noTieCustomer><span class="text-danger">Not matched</span></ng-template>
						</td>
						<td>{{ tie.debtorKey }}</td>
						<td>
							<span [ngClass]="tie.debtorKey ? 'text-success' : 'text-danger font-weight-bold'">{{ tieStatus(tie) }}</span>
						</td>
						<td class="text-nowrap">{{ tie.importedDate | date : 'short' }}</td>
					</tr>
				</tbody>
			</table>
		</div>
	</div>
</div>

</ng-container>

<ng-container *ngIf="section === 'clientnotes' || section === 'invoicenotes' || section === 'customernotes'">
<!-- WinFactor client notes (WinFactor.ClientNotes) / invoice notes (WinFactor.InvoiceNotes) / customer notes (WinFactor.CustomerNotes) file -->
<div class="col-lg-12 mb-4">
	<div class="shadow p-3">
		<div class="d-flex justify-content-between align-items-start">
			<div>
				<h4 class="mb-1">{{ section === 'clientnotes' ? 'Client Notes File' : section === 'customernotes' ? 'Customer Notes File' : 'Invoice Notes File' }}</h4>
				<small class="text-muted" *ngIf="section === 'clientnotes'">
					Upload the WinFactor client notes export (.csv, .xlsx or .xls, first sheet: ClientID, ClientName, Date, User, NoteType, Note). Every
					client in the file has its notes in WinFactor.ClientNotes replaced by the file's; other clients' notes are left alone. A row repeated
					exactly is saved once. The conversion sends a client's notes as its FactorCloud General Notes (step 8).
				</small>
				<small class="text-muted" *ngIf="section === 'invoicenotes'">
					Upload the WinFactor invoice notes export (.xlsx, .xls or .csv, first sheet: ClientID, ClientName, InvoiceNumber, LoadNumber,
					InvoiceDate, InvoiceAmount, Paid, Customer, AdvanceNo, NoteDate, User, NoteType, Note, ShowInPortal, WF_InvoiceID). Every client in
					the file has its notes in WinFactor.InvoiceNotes replaced by the file's; other clients' notes are left alone. A row repeated exactly is
					saved once. Once a client's invoices are in FactorCloud, the conversion adds each invoice's notes to it (step 18).
				</small>
				<small class="text-muted" *ngIf="section === 'customernotes'">
					Upload the WinFactor customer notes export (.csv, .xlsx or .xls, first sheet: CustomerID, CustomerName, NoteDate, UserName, NoteType,
					Note); the export can be loaded one part at a time. Every customer in the file has its notes in WinFactor.CustomerNotes replaced by the
					file's (notes that did not change keep what was already sent to FactorCloud); other customers' notes are left alone. A row repeated
					exactly is saved once. A customer's notes go to its FactorCloud debtor (Aladdin debtor with WinFactorID = CustomerID) as General Notes,
					below or in conversion step 9.
				</small>
			</div>
			<div class="text-right text-nowrap ml-3" *ngIf="summary">
				<div *ngIf="section === 'clientnotes'">
					<b>{{ summary.clientNoteCount | number }}</b> note(s) for <b>{{ summary.clientNoteClientCount | number }}</b> client(s) saved
				</div>
				<div *ngIf="section === 'invoicenotes'">
					<b>{{ summary.invoiceNoteCount | number }}</b> note(s) for <b>{{ summary.invoiceNoteClientCount | number }}</b> client(s) saved
				</div>
				<ng-container *ngIf="section === 'customernotes'">
					<div>
						<b>{{ summary.customerNoteCount | number }}</b> note(s) for <b>{{ summary.customerNoteCustomerCount | number }}</b> customer(s) saved
					</div>
					<div class="text-muted">
						<b>{{ summary.customerNoteSentCustomerCount | number }}</b> customer(s) sent to FactorCloud
					</div>
				</ng-container>
			</div>
		</div>

		<div class="row mt-3">
			<div class="col-md-12" *ngIf="!atoBFile">
				<div
					ng2FileDrop
					[ngClass]="fileDragged ? 'nv-file-over' : ''"
					[uploader]="atoBUploader"
					class="conversion-drop-zone"
					(dragover)="onDragover()"
					(dragleave)="onDragout()"
					(mouseleave)="onDragout()"
					data-testid="conversion-data-upload-notes-drop">
					<label
						for="conversion-notes-file"
						role="button"
						aria-label="Choose the notes file"
						class="conversion-file-label m-0 w-100 h-100 d-flex align-items-center justify-content-center">
						<i class="fa fa-cloud-upload mr-2" aria-hidden="true"></i> Drop the WinFactor {{ section === 'clientnotes' ? 'client' : section === 'customernotes' ? 'customer' : 'invoice' }} notes file here,
						or click to choose it
					</label>
					<input
						id="conversion-notes-file"
						type="file"
						accept=".xlsx,.xls,.csv"
						ng2FileSelect
						[uploader]="atoBUploader"
						data-testid="conversion-data-upload-notes-file" />
				</div>
			</div>

			<div class="col-md-3" *ngIf="atoBFile">
				<div class="text-muted">File</div>
				<div class="font-weight-bold conversion-ellipsis">{{ atoBFile.name }}</div>
				<div class="text-muted mt-2">Size</div>
				<div class="font-weight-bold">{{ atoBFile.size / 1024 | number : '1.0-1' }} KB</div>
				<ng-container *ngIf="previewColumns.length > 0">
					<div class="text-muted mt-2">Rows</div>
					<div class="font-weight-bold">{{ previewTotalRows | number }}</div>
				</ng-container>
				<button
					type="button"
					class="btn btn-primary mt-3 mr-2"
					[disabled]="uploadingNotes"
					(click)="uploadNotes()"
					data-testid="conversion-data-upload-notes-upload">
					<i class="fa" [ngClass]="uploadingNotes ? 'fa-spinner fa-spin' : 'fa-upload'" aria-hidden="true"></i>
					Upload
				</button>
				<button
					type="button"
					class="btn btn-secondary mt-3"
					[disabled]="uploadingNotes"
					(click)="resetAtoB()"
					data-testid="conversion-data-upload-notes-cancel">
					Cancel
				</button>
			</div>

			<div class="col-md-9" *ngIf="atoBFile && previewColumns.length > 0">
				<div class="font-weight-bold">
					Preview
					<small class="text-muted" *ngIf="previewTotalRows > previewRows.length">(first {{ previewRows.length }} of {{ previewTotalRows }} rows)</small>
				</div>
				<ngx-datatable
					class="material striped conversion-preview"
					[rows]="previewRows"
					[headerHeight]="40"
					[footerHeight]="0"
					[rowHeight]="32"
					[scrollbarH]="true"
					[scrollbarV]="true">
					<ngx-datatable-column *ngFor="let column of previewColumns" [name]="column.name" [prop]="column.prop" [width]="140"></ngx-datatable-column>
				</ngx-datatable>
			</div>
			<div class="col-md-9 text-muted" *ngIf="atoBFile && previewColumns.length === 0">
				Excel files are read when you upload them; there is no preview.
			</div>
		</div>

		<ng-container *ngTemplateOutlet="resultTemplate; context: { $implicit: notesResult }"></ng-container>
	</div>
</div>

<!-- Send customer notes to FactorCloud -->
<div class="col-lg-12 mb-4" *ngIf="section === 'customernotes'">
	<div class="shadow p-3">
		<h4 class="mb-1">Send to FactorCloud</h4>
		<small class="text-muted">
			Each customer's notes go to its FactorCloud debtor as General Notes (Popup notes first, then by note date, "MM/dd/yyyy UserName (NoteType): Note"; 1/1/1900 dates are
			left off). FactorCloud debtors are shared between clients, so a debtor whose General Notes were not written by the conversion is left
			alone; a customer with new notes since it was sent has its notes sent again. Customers whose Aladdin debtor is not in FactorCloud yet are
			sent by conversion step 9 when a carrier that uses them is converted.
		</small>

		<div class="d-flex justify-content-between align-items-center mt-3">
			<div class="d-flex align-items-center">
				<input
					class="form-control conversion-client-filter mr-2"
					type="text"
					[(ngModel)]="noteCustomerFilter"
					placeholder="Customer ID or name, FactorCloud id..."
					data-testid="conversion-data-upload-note-customers-filter" />
				<select
					class="form-control mr-2"
					[(ngModel)]="noteCustomerStatus"
					aria-label="Status"
					data-testid="conversion-data-upload-note-customers-status">
					<option value="all">All</option>
					<option value="ready">Ready to send</option>
					<option value="sent">Sent</option>
					<option value="notinfc">Not in FactorCloud</option>
				</select>
			</div>
			<div class="d-flex align-items-center">
				<span *ngIf="sendingCustomerNotes" class="text-muted mr-3" data-testid="conversion-data-upload-note-customers-progress">
					<i class="fa-solid fa-spinner fa-spin"></i> Sending {{ customerNoteSendDone }} / {{ customerNoteSendTotal }}
				</span>
				<button
					type="button"
					class="btn btn-outline-primary btn-round mr-2"
					title="Refresh"
					[disabled]="loadingNoteCustomers || sendingCustomerNotes"
					(click)="loadNoteCustomers()"
					data-testid="conversion-data-upload-note-customers-refresh">
					<i class="fa-solid fa-rotate-right"></i>
				</button>
				<button
					*ngIf="sendingCustomerNotes"
					type="button"
					class="btn btn-secondary mr-2"
					(click)="stopSendingCustomerNotes()"
					data-testid="conversion-data-upload-note-customers-stop">
					Stop
				</button>
				<button
					type="button"
					class="btn btn-primary mr-2"
					[disabled]="sendingCustomerNotes || selectedNoteCustomerIds.length === 0"
					(click)="sendSelectedCustomerNotes()"
					data-testid="conversion-data-upload-note-customers-send-selected">
					Send Selected
					<span *ngIf="selectedNoteCustomerIds.length > 0">({{ selectedNoteCustomerIds.length }})</span>
				</button>
				<button
					type="button"
					class="btn btn-primary"
					[disabled]="sendingCustomerNotes || noteCustomerReadyCount === 0"
					(click)="sendAllReadyCustomerNotes()"
					data-testid="conversion-data-upload-note-customers-send-all">
					Send All Ready ({{ noteCustomerReadyCount | number }})
				</button>
			</div>
		</div>

		<div class="text-muted mt-2">
			{{ filteredNoteCustomers.length | number }} of {{ noteCustomers.length | number }} customer(s)
			<span *ngIf="filteredNoteCustomers.length > noteCustomerRowLimit">(first {{ noteCustomerRowLimit }} shown)</span>
		</div>

		<div class="conversion-table-scroll mt-2">
			<table class="table table-sm table-hover mb-0">
				<thead>
					<tr>
						<th class="conversion-check-col">
							<input
								type="checkbox"
								[checked]="allShownReadyNoteCustomersSelected"
								[disabled]="sendingCustomerNotes"
								(change)="toggleShownReadyNoteCustomers(!allShownReadyNoteCustomersSelected)"
								data-testid="conversion-data-upload-note-customers-all" />
						</th>
						<th>Customer ID</th>
						<th>WinFactor Name</th>
						<th>Aladdin Debtor</th>
						<th>FactorCloud Debtor</th>
						<th class="text-right">Notes</th>
						<th class="text-right">Sent</th>
						<th>Status</th>
					</tr>
				</thead>
				<tbody>
					<tr *ngIf="loadingNoteCustomers">
						<td colspan="8" class="text-muted"><i class="fa-solid fa-spinner fa-spin"></i> Loading...</td>
					</tr>
					<tr *ngIf="!loadingNoteCustomers && filteredNoteCustomers.length === 0">
						<td colspan="8" class="text-muted">No customers found. Upload the customer notes file above first.</td>
					</tr>
					<tr *ngFor="let customer of shownNoteCustomers; let i = index" [attr.data-testid]="'conversion-data-upload-note-customer-' + i">
						<td class="conversion-check-col">
							<input
								type="checkbox"
								[(ngModel)]="selectedNoteCustomers[customer.customerId]"
								[disabled]="sendingCustomerNotes || !isNoteCustomerReady(customer)"
								[attr.data-testid]="'conversion-data-upload-note-customer-select-' + i" />
						</td>
						<td>{{ customer.customerId }}</td>
						<td>{{ customer.customerName }}</td>
						<td>
							<span *ngIf="customer.debtorKey">{{ customer.debtorName }} <small class="text-muted">({{ customer.debtorKey }})</small></span>
						</td>
						<td class="conversion-ellipsis" [title]="customer.factorCloudDebtorId || ''">{{ customer.factorCloudDebtorId }}</td>
						<td class="text-right">{{ customer.noteCount }}</td>
						<td class="text-right" [class.text-success]="customer.sentNoteCount === customer.noteCount">{{ customer.sentNoteCount }}</td>
						<td>
							<span
								*ngIf="noteCustomerResults[customer.customerId] as result"
								[class.text-success]="result.status === 'Sent' || result.status === 'AlreadySent'"
								[class.text-warning]="result.status === 'Skipped'"
								[class.text-danger]="result.status === 'Failed' || result.status === 'NotInFactorCloud'"
								[title]="result.message">
								{{ result.status === 'Skipped' ? 'Left alone' : result.status }}
							</span>
							<span
								*ngIf="!noteCustomerResults[customer.customerId]"
								[class.text-success]="noteCustomerState(customer) === 'Sent'"
								[class.text-muted]="noteCustomerState(customer) === 'No Aladdin debtor' || noteCustomerState(customer) === 'No FactorCloud debtor'">
								{{ noteCustomerState(customer) }}
							</span>
						</td>
					</tr>
				</tbody>
			</table>
		</div>

		<div *ngIf="!sendingCustomerNotes && customerNoteSendFailures.length > 0" class="mt-3" data-testid="conversion-data-upload-note-customers-failures">
			<div class="text-danger font-weight-bold">Not sent ({{ customerNoteSendFailures.length }})</div>
			<ul class="mb-0">
				<li *ngFor="let failure of customerNoteSendFailures">
					{{ failure.customerId }}<span *ngIf="failure.customerName"> {{ failure.customerName }}</span>: {{ failure.message }}
				</li>
			</ul>
		</div>
	</div>
</div>

<!-- Saved notes -->
<div class="col-lg-12 mb-4">
	<div class="shadow p-3">
		<h4 class="mb-1">{{ section === 'clientnotes' ? 'Saved Client Notes' : section === 'customernotes' ? 'Saved Customer Notes' : 'Saved Invoice Notes' }}</h4>
		<small class="text-muted">
			The rows in {{ section === 'clientnotes' ? 'WinFactor.ClientNotes' : section === 'customernotes' ? 'WinFactor.CustomerNotes' : 'WinFactor.InvoiceNotes' }} (the first {{ relationshipRowLimit }} matching the search).
		</small>

		<div class="d-flex justify-content-between align-items-center mt-3">
			<div class="d-flex align-items-center">
				<input
					class="form-control conversion-client-filter mr-2"
					type="text"
					[(ngModel)]="noteSearch"
					(keyup.enter)="loadNotes()"
					[placeholder]="section === 'clientnotes' ? 'Client ID or name, user, type, note...' : section === 'customernotes' ? 'Customer ID or name, user, type, note...' : 'Client ID or name, invoice #, load #, WF invoice ID...'"
					data-testid="conversion-data-upload-notes-search" />
				<button
					type="button"
					class="btn btn-primary"
					[disabled]="loadingNotes"
					(click)="loadNotes()"
					data-testid="conversion-data-upload-notes-search-button">
					Search
				</button>
			</div>
			<span class="text-muted">{{ (section === 'clientnotes' ? clientNotes.length : section === 'customernotes' ? customerNotes.length : invoiceNotes.length) | number }} shown</span>
		</div>

		<div class="conversion-table-scroll mt-2">
			<table class="table table-sm table-hover mb-0" *ngIf="section === 'clientnotes'">
				<thead>
					<tr>
						<th>Client ID</th>
						<th>Client Name</th>
						<th>Date</th>
						<th>User</th>
						<th>Type</th>
						<th>Note</th>
					</tr>
				</thead>
				<tbody>
					<tr *ngIf="loadingNotes">
						<td colspan="6" class="text-muted"><i class="fa-solid fa-spinner fa-spin"></i> Loading...</td>
					</tr>
					<tr *ngIf="!loadingNotes && clientNotes.length === 0">
						<td colspan="6" class="text-muted">No client notes found.</td>
					</tr>
					<tr *ngFor="let note of clientNotes; let i = index" [attr.data-testid]="'conversion-data-upload-client-note-' + i">
						<td>{{ note.clientId }}</td>
						<td>{{ note.clientName }}</td>
						<td class="text-nowrap">{{ note.noteDate | date : 'shortDate' }}</td>
						<td>{{ note.userName }}</td>
						<td>{{ note.noteType }}</td>
						<td class="conversion-note-text">{{ note.note }}</td>
					</tr>
				</tbody>
			</table>

			<table class="table table-sm table-hover mb-0" *ngIf="section === 'customernotes'">
				<thead>
					<tr>
						<th>Customer ID</th>
						<th>Customer Name</th>
						<th>Date</th>
						<th>User</th>
						<th>Type</th>
						<th>Note</th>
						<th>Sent to FactorCloud</th>
					</tr>
				</thead>
				<tbody>
					<tr *ngIf="loadingNotes">
						<td colspan="7" class="text-muted"><i class="fa-solid fa-spinner fa-spin"></i> Loading...</td>
					</tr>
					<tr *ngIf="!loadingNotes && customerNotes.length === 0">
						<td colspan="7" class="text-muted">No customer notes found.</td>
					</tr>
					<tr *ngFor="let note of customerNotes; let i = index" [attr.data-testid]="'conversion-data-upload-customer-note-' + i">
						<td>{{ note.customerId }}</td>
						<td>{{ note.customerName }}</td>
						<td class="text-nowrap">{{ note.noteDate | date : 'shortDate' }}</td>
						<td>{{ note.userName }}</td>
						<td>{{ note.noteType }}</td>
						<td class="conversion-note-text">{{ note.note }}</td>
						<td class="text-nowrap" [title]="note.factorCloudDebtorId || ''">{{ note.sentToFactorCloudDate | date : 'short' }}</td>
					</tr>
				</tbody>
			</table>

			<table class="table table-sm table-hover mb-0" *ngIf="section === 'invoicenotes'">
				<thead>
					<tr>
						<th>Client ID</th>
						<th>Client Name</th>
						<th>Invoice #</th>
						<th>Load #</th>
						<th>WF Invoice ID</th>
						<th>Customer</th>
						<th>Note Date</th>
						<th>User</th>
						<th>Type</th>
						<th>Note</th>
					</tr>
				</thead>
				<tbody>
					<tr *ngIf="loadingNotes">
						<td colspan="10" class="text-muted"><i class="fa-solid fa-spinner fa-spin"></i> Loading...</td>
					</tr>
					<tr *ngIf="!loadingNotes && invoiceNotes.length === 0">
						<td colspan="10" class="text-muted">No invoice notes found.</td>
					</tr>
					<tr *ngFor="let note of invoiceNotes; let i = index" [attr.data-testid]="'conversion-data-upload-invoice-note-' + i">
						<td>{{ note.clientId }}</td>
						<td>{{ note.clientName }}</td>
						<td>{{ note.invoiceNumber }}</td>
						<td>{{ note.loadNumber }}</td>
						<td>{{ note.wfInvoiceId }}</td>
						<td>{{ note.customer }}</td>
						<td class="text-nowrap">{{ note.noteDate | date : 'short' }}</td>
						<td>{{ note.userName }}</td>
						<td>{{ note.noteType }}</td>
						<td class="conversion-note-text">{{ note.note }}</td>
					</tr>
				</tbody>
			</table>
		</div>
	</div>
</div>

</ng-container>

<ng-container *ngIf="isDocumentsSection">
<!-- Client / customer documents in blob storage -->
<div class="col-lg-12 mb-4">
	<div class="shadow p-3">
		<div class="d-flex justify-content-between align-items-start">
			<div>
				<h4 class="mb-1">{{ documentsTitle }}</h4>
				<small class="text-muted" *ngIf="!customerDocuments">
					Reads the WinFactor client documents from blob storage (the WinFactor import container, conversion-documents folder,
					one folder per client) into WinFactor.ClientDocuments. Only the document details are saved; the files stay in blob storage.
				</small>
				<small class="text-muted" *ngIf="customerDocuments">
					Reads the WinFactor customer documents from blob storage (the WinFactor import container, conversion-customer-documents folder,
					one folder per customer, "CustomerID - name") into WinFactor.CustomerDocuments. Only the document details are saved; the files
					stay in blob storage. The conversion also sends each converted carrier's debtors' documents (step 11).
				</small>
			</div>
			<div class="text-right text-nowrap ml-3" *ngIf="summary && !customerDocuments">
				<div><b>{{ summary.clientDocumentCount | number }}</b> document(s) for <b>{{ summary.clientDocumentClientCount | number }}</b> client(s)</div>
				<small class="text-muted" *ngIf="summary.clientDocumentLastImportedDate">
					Last import {{ summary.clientDocumentLastImportedDate | date : 'short' }}
				</small>
			</div>
			<div class="text-right text-nowrap ml-3" *ngIf="summary && customerDocuments">
				<div><b>{{ summary.customerDocumentCount | number }}</b> document(s) for <b>{{ summary.customerDocumentCustomerCount | number }}</b> customer(s)</div>
				<small class="text-muted" *ngIf="summary.customerDocumentLastImportedDate">
					Last import {{ summary.customerDocumentLastImportedDate | date : 'short' }}
				</small>
			</div>
		</div>

		<button
			type="button"
			class="btn btn-primary mt-3"
			[disabled]="importingDocuments"
			(click)="importDocuments()"
			data-testid="conversion-data-upload-documents-import">
			<i class="fa" [ngClass]="importingDocuments ? 'fa-spinner fa-spin' : 'fa-cloud-download'" aria-hidden="true"></i>
			Import from Blob Storage
		</button>

		<ng-container *ngTemplateOutlet="resultTemplate; context: { $implicit: documentsResult }"></ng-container>
	</div>
</div>

<!-- Send the documents to FactorCloud -->
<div class="col-lg-12 mb-4">
	<div class="shadow p-3">
		<div class="d-flex justify-content-between align-items-start">
			<div>
				<h4 class="mb-1">Send Documents to FactorCloud</h4>
				<small class="text-muted" *ngIf="!customerDocuments">
					Pick a client to see its documents and send the ones you tick, or tick clients and send all of their documents at once.
					Documents go to the carrier's FactorCloud client, so the carrier has to be converted first. A document already in FactorCloud is never sent again.
					The type comes from the file name: NOA or Notice of Assignment = NOA; UCC = UCC; Factoring Agreement, Ownership Agreement or
					Factoring Amendment = CONTRACT; anything else = OTHER.
				</small>
				<small class="text-muted" *ngIf="customerDocuments">
					Pick a customer to see its documents and send the ones you tick, or tick customers and send all of their documents at once.
					Documents go to the customer's FactorCloud debtor (its WinFactor debtor in Aladdin, WinFactorID = CustomerID), so the debtor has to be in
					FactorCloud first. A document already in FactorCloud is never sent again.
					The type comes from the file name: NOA or Notice of Assignment = NOA; UCC = UCC; Factoring Agreement, Ownership Agreement or
					Factoring Amendment = CONTRACT; anything else = OTHER.
				</small>
			</div>
		</div>

		<div class="d-flex justify-content-between align-items-center mt-3">
			<input
				class="form-control conversion-client-filter"
				type="text"
				[(ngModel)]="clientFilter"
				[placeholder]="customerDocuments ? 'Customer ID or name...' : 'Client ID or name...'"
				data-testid="conversion-data-upload-client-filter" />
			<div class="d-flex align-items-center">
				<span *ngIf="sending" class="text-muted mr-3" data-testid="conversion-data-upload-send-progress">
					<i class="fa-solid fa-spinner fa-spin"></i> Sending {{ sendDone }} / {{ sendTotal }}
				</span>
				<button
					type="button"
					class="btn btn-outline-primary btn-round mr-2"
					title="Refresh"
					[disabled]="loadingDocumentClients || sending"
					(click)="loadDocumentClients()"
					data-testid="conversion-data-upload-clients-refresh">
					<i class="fa-solid fa-rotate-right"></i>
				</button>
				<button
					type="button"
					class="btn btn-primary"
					[disabled]="sending || selectedDocumentClients.length === 0"
					(click)="sendSelectedClients()"
					data-testid="conversion-data-upload-send-clients">
					Send All Documents for Selected {{ customerDocuments ? 'Customers' : 'Clients' }}
					<span *ngIf="selectedDocumentClients.length > 0">({{ selectedDocumentClients.length }})</span>
				</button>
			</div>
		</div>

		<div class="conversion-table-scroll mt-2">
			<table class="table table-sm table-hover mb-0">
				<thead>
					<tr>
						<th class="conversion-check-col">
							<input
								type="checkbox"
								[checked]="allFilteredClientsSelected"
								[disabled]="sending"
								(change)="toggleAllClients(!allFilteredClientsSelected)"
								data-testid="conversion-data-upload-clients-all" />
						</th>
						<th>{{ customerDocuments ? 'Customer ID' : 'Client ID' }}</th>
						<th>Folder</th>
						<th>{{ customerDocuments ? 'Debtor' : 'Carrier' }}</th>
						<th>In FactorCloud</th>
						<th class="text-right">Documents</th>
						<th class="text-right">Sent</th>
						<th></th>
					</tr>
				</thead>
				<tbody>
					<tr *ngIf="loadingDocumentClients">
						<td colspan="8" class="text-muted"><i class="fa-solid fa-spinner fa-spin"></i> Loading...</td>
					</tr>
					<tr *ngIf="!loadingDocumentClients && filteredDocumentClients.length === 0">
						<td colspan="8" class="text-muted">No documents yet. Use Import from Blob Storage above first.</td>
					</tr>
					<tr
						*ngFor="let client of filteredDocumentClients; let i = index"
						[class.table-active]="currentClient === client"
						[attr.data-testid]="'conversion-data-upload-client-' + i">
						<td class="conversion-check-col">
							<input
								type="checkbox"
								[(ngModel)]="client.selected"
								[disabled]="sending"
								[attr.data-testid]="'conversion-data-upload-client-select-' + i" />
						</td>
						<td>{{ client.clientId }}</td>
						<td class="conversion-ellipsis conversion-folder-col" [title]="client.clientFolderName">{{ client.clientFolderName }}</td>
						<td>{{ client.clientName }}</td>
						<td>
							<span *ngIf="client.factorCloudClientId" class="text-success">Yes</span>
							<span *ngIf="!client.factorCloudClientId" class="text-muted">{{ customerDocuments ? 'Not in FactorCloud' : 'Not converted' }}</span>
						</td>
						<td class="text-right">{{ client.documentCount }}</td>
						<td class="text-right" [class.text-success]="client.sentCount === client.documentCount">{{ client.sentCount }}</td>
						<td class="text-right">
							<button type="button" class="btn btn-primary btn-sm py-0" (click)="viewClient(client)" [attr.data-testid]="'conversion-data-upload-view-' + i">
								Documents
							</button>
						</td>
					</tr>
				</tbody>
			</table>
		</div>

		<!-- The chosen client's documents -->
		<div *ngIf="currentClient" class="mt-4">
			<div class="d-flex justify-content-between align-items-center">
				<h5 class="mb-0">
					{{ currentClient.clientFolderName || (customerDocuments ? 'Documents outside a customer folder' : 'Documents outside a client folder') }}
					<small class="text-muted" *ngIf="!currentClient.factorCloudClientId">(not in FactorCloud yet)</small>
				</h5>
				<button
					type="button"
					class="btn btn-primary"
					[disabled]="sending || selectedClientDocuments.length === 0 || !currentClient.factorCloudClientId"
					(click)="sendSelectedDocuments()"
					data-testid="conversion-data-upload-send-documents">
					Send Selected Documents
					<span *ngIf="selectedClientDocuments.length > 0">({{ selectedClientDocuments.length }})</span>
				</button>
			</div>
			<div class="conversion-table-scroll mt-2">
				<table class="table table-sm mb-0">
					<thead>
						<tr>
							<th class="conversion-check-col">
								<input
									type="checkbox"
									[checked]="allUnsentDocumentsSelected"
									[disabled]="sending || unsentClientDocuments.length === 0"
									(change)="toggleAllDocuments(!allUnsentDocumentsSelected)"
									data-testid="conversion-data-upload-documents-all" />
							</th>
							<th>Document</th>
							<th class="text-right">Size</th>
							<th>Modified</th>
							<th>FactorCloud</th>
						</tr>
					</thead>
					<tbody>
						<tr *ngIf="loadingClientDocuments">
							<td colspan="5" class="text-muted"><i class="fa-solid fa-spinner fa-spin"></i> Loading...</td>
						</tr>
						<tr *ngFor="let document of clientDocuments; let i = index" [attr.data-testid]="'conversion-data-upload-document-' + i">
							<td class="conversion-check-col">
								<input
									type="checkbox"
									[(ngModel)]="document.selected"
									[disabled]="sending || !!document.sentToFactorCloudDate"
									[attr.data-testid]="'conversion-data-upload-document-select-' + i" />
							</td>
							<td class="conversion-ellipsis" [title]="document.blobPath">{{ document.documentFileName }}</td>
							<td class="text-right text-nowrap">{{ (document.fileSizeBytes || 0) / 1024 | number : '1.0-0' }} KB</td>
							<td class="text-nowrap">{{ document.blobLastModifiedDate | date : 'short' }}</td>
							<td>
								<span *ngIf="document.sentToFactorCloudDate" class="text-success">
									Sent {{ document.sentToFactorCloudDate | date : 'short' }} ({{ document.factorCloudDocumentType }})
								</span>
								<span *ngIf="!document.sentToFactorCloudDate && document.sendStatus === 'Failed'" class="text-danger" [title]="document.sendMessage">
									Failed: {{ document.sendMessage }}
								</span>
								<span *ngIf="!document.sentToFactorCloudDate && document.sendStatus !== 'Failed'" class="text-muted">Not sent</span>
							</td>
						</tr>
					</tbody>
				</table>
			</div>
		</div>

		<!-- Failures from the last send -->
		<div class="mt-3" *ngIf="!sending && sendFailures.length > 0">
			<div class="text-danger font-weight-bold">{{ sendFailures.length }} document(s) failed</div>
			<ul class="mb-0 conversion-problems">
				<li *ngFor="let failure of sendFailures">{{ failure.documentFileName || ('Document ' + failure.documentKey) }}: {{ failure.message }}</li>
			</ul>
		</div>
	</div>
</div>

</ng-container>

<ng-template #resultTemplate let-result>
	<div class="mt-3" *ngIf="result">
		<div class="text-success font-weight-bold">{{ result.summary }}</div>
		<div *ngIf="result.problems && result.problems.length > 0" class="mt-2">
			<div class="text-warning font-weight-bold">Notes ({{ result.problems.length }})</div>
			<ul class="mb-0 conversion-problems">
				<li *ngFor="let problem of result.problems">{{ problem }}</li>
			</ul>
		</div>
	</div>
</ng-template>



// WEBPACK FOOTER //
// ./src/app/portal/conversion/data-upload/conversion-data-upload.component.html